import { useMemo } from "react";
import { ListTree, PanelLeftClose, PanelLeftOpen } from "lucide-react";

/**
 * Left-side index navigator. Shows a grouped list of slides when open and a
 * column of tiny dot-ticks when collapsed.
 */
export function DeckSidebar({ deck, idx, go, onOpenOverview, sidebarOpen, setSidebarOpen, prospect }) {
  const titles = useMemo(
    () => deck.map((d, i) => ({ i, t: d.t, group: d.group })),
    [deck],
  );

  const grouped = useMemo(() => {
    const m = new Map();
    titles.forEach((t) => {
      if (!m.has(t.group)) m.set(t.group, []);
      m.get(t.group).push(t);
    });
    return [...m.entries()];
  }, [titles]);

  const sidebarW = sidebarOpen ? "w-64" : "w-12";

  return (
    <aside
      className={`relative z-30 h-full border-r border-ink-900/10 bg-white/55 backdrop-blur-sm transition-[width] duration-200 ${sidebarW} flex flex-col`}
      data-testid="deck-sidebar"
    >
      <div className="flex items-center justify-between px-3 py-3 border-b border-ink-900/10">
        {sidebarOpen ? (
          <div className="flex items-center gap-2 min-w-0">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <div className="serif text-sm leading-tight truncate">CompliSun</div>
              <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 truncate">
                {prospect ? `For ${prospect.company_name}` : "Partner Deck"}
              </div>
            </div>
          </div>
        ) : (
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain mx-auto" />
        )}
        <button
          onClick={() => setSidebarOpen((s) => !s)}
          data-testid="sidebar-toggle"
          title={sidebarOpen ? "Collapse" : "Expand"}
          className="h-7 w-7 grid place-items-center rounded-md text-ink-900/55 hover:text-sun-700 hover:bg-cream-200/50 transition-colors flex-shrink-0"
        >
          {sidebarOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {sidebarOpen ? (
          grouped.map(([group, items]) => (
            <div key={group} className="mb-2">
              <div className="px-4 pt-2 pb-1 mono text-[9px] uppercase tracking-[0.22em] text-sun-700">{group}</div>
              <ul>
                {items.map((s) => {
                  const active = s.i === idx;
                  return (
                    <li key={s.i}>
                      <button
                        onClick={() => go(s.i)}
                        data-testid={`sidebar-slide-${s.i}`}
                        className={`group w-full text-left flex items-center gap-2.5 px-4 py-1.5 text-sm transition-colors
                          ${active
                            ? "bg-sun-500/15 text-ink-900 border-l-2 border-sun-500"
                            : "text-ink-900/65 hover:text-ink-900 hover:bg-cream-200/40 border-l-2 border-transparent"}`}
                      >
                        <span className={`mono text-[10px] tracking-[0.1em] ${active ? "text-sun-700" : "text-ink-900/40"}`}>
                          {String(s.i + 1).padStart(2, "0")}
                        </span>
                        <span className="truncate">{s.t}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center gap-1 py-2">
            {titles.map((s) => (
              <button
                key={s.i}
                onClick={() => go(s.i)}
                title={s.t}
                data-testid={`sidebar-tick-${s.i}`}
                className={`h-1.5 w-6 rounded-full transition-colors ${s.i === idx ? "bg-sun-500" : "bg-ink-900/15 hover:bg-ink-900/35"}`}
              />
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-ink-900/10 p-2">
        <button
          onClick={onOpenOverview}
          data-testid="sidebar-overview"
          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-ink-900/65 hover:text-ink-900 hover:bg-cream-200/50 mono text-[10px] uppercase tracking-[0.22em] transition-colors ${sidebarOpen ? "justify-start" : "justify-center"}`}
          title="Overview (Esc)"
        >
          <ListTree className="h-3.5 w-3.5 flex-shrink-0" />
          {sidebarOpen && <span>Overview</span>}
        </button>
      </div>
    </aside>
  );
}
